import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Header,
  SpaceBetween,
  Box,
  Button,
  Table,
  StatusIndicator,
  Spinner,
  BreadcrumbGroup,
  Alert,
  ColumnLayout,
  Modal,
  Select,
  FileUpload,
  ProgressBar,
  Icon,
  Textarea,
  FormField,
  Input,
  TextFilter,
} from '@cloudscape-design/components';
import axios from 'axios';
import { useAuth } from '../AuthContext';
import { useNotifications } from './NotificationContext';
import AppLayoutWrapper from './AppLayoutWrapper';

const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return '—';
  const num = typeof bytes === 'string' ? parseInt(bytes, 10) : bytes;
  if (Number.isNaN(num)) return String(bytes);
  if (num === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(num) / Math.log(k));
  return `${(num / k ** i).toFixed(i > 0 ? 2 : 0)} ${sizes[i]}`;
};

export default function KnowledgeBaseList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { error: notifyError, success: notifySuccess } = useNotifications();
  const [kbs, setKbs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [selectedKb, setSelectedKb] = useState(null);
  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [filesModalVisible, setFilesModalVisible] = useState(false);
  const [files, setFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileDeleteModalVisible, setFileDeleteModalVisible] = useState(false);
  const [vectorOnly, setVectorOnly] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', description: '', status: 'active' });
  const [uploadQueue, setUploadQueue] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [pendingUploadFiles, setPendingUploadFiles] = useState([]);
  const [fileDetails, setFileDetails] = useState({});
  const [retryingId, setRetryingId] = useState(null);
  const [filteringText, setFilteringText] = useState('');
  const [sortingColumn, setSortingColumn] = useState(null);
  const [sortingDescending, setSortingDescending] = useState(true);

  const filteredKbs = useMemo(() => {
    if (!filteringText) return kbs;
    const lower = filteringText.toLowerCase();
    return kbs.filter((kb) =>
      (kb.name && kb.name.toLowerCase().includes(lower)) ||
      (kb.description && kb.description.toLowerCase().includes(lower))
    );
  }, [kbs, filteringText]);

  const sortedKbs = useMemo(() => {
    if (!sortingColumn) return filteredKbs;
    const sorted = [...filteredKbs].sort((a, b) => {
      const f = sortingColumn.sortingField;
      const getVal = (item) => {
        if (f === 'name') return item.name || '';
        if (f === 'description') return item.description || '';
        if (f === 'status') return item.sync_status || item.is_active || '';
        return '';
      };
      const aVal = getVal(a);
      const bVal = getVal(b);
      if (aVal < bVal) return -1;
      if (aVal > bVal) return 1;
      return 0;
    });
    return sortingDescending ? [...sorted].reverse() : sorted;
  }, [filteredKbs, sortingColumn, sortingDescending]);

  const accessToken = user?.access_token || user?.token;

  const fetchKBs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      if (!accessToken) {
        setLoading(false);
        return;
      }
      const response = await axios.get('/api/knowledge-bases/', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const raw = response.data?.data || response.data || [];
      setKbs(Array.isArray(raw) ? raw : []);
    } catch (err) {
      console.error('KB fetch error:', err);
      setError('Unable to load knowledge bases.');
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    if (user) fetchKBs();
  }, [user, fetchKBs]);

  const handleRetrySync = async (kb) => {
    const kbId = kb.id;
    if (!kbId) return;
    setRetryingId(kbId);
    try {
      const resp = await axios.post(`/api/knowledge-bases/${kbId}/retry-sync/`, {}, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (resp.data?.status === 'success') {
        notifySuccess('KB synced to adexcloud successfully');
        fetchKBs();
      } else {
        notifyError(resp.data?.message || 'Sync retry failed');
      }
    } catch (err) {
      notifyError('Retry sync failed: ' + (err.response?.data?.message || err.message));
    } finally {
      setRetryingId(null);
    }
  };

  const effectiveKbId = (kb) => kb.id || kb.adexcloud_kb_id;

  const handleDelete = async () => {
    if (!selectedKb) return;
    const kbId = effectiveKbId(selectedKb);
    if (!kbId) {
      notifyError('Cannot delete: KB has no valid ID');
      return;
    }
    try {
      await axios.delete(`/api/knowledge-bases/${kbId}/delete/`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setKbs((prev) => prev.filter((k) => effectiveKbId(k) !== kbId));
      setDeleteModalVisible(false);
      notifySuccess('Knowledge base deleted');
    } catch (err) {
      notifyError('Delete failed: ' + (err.response?.data?.message || err.message));
    }
  };

  const openAssign = async (kb) => {
    setSelectedKb(kb);
    setAssignModalVisible(true);
    try {
      const res = await axios.get('/api/agents/for-assignment/', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const agentList = res.data?.data || [];
      setAgents(agentList.map((a) => ({ label: a.name || 'Unnamed Agent', value: a.id })));
    } catch (err) {
      console.error('Agent fetch error:', err);
    }
  };

  const handleAssign = async () => {
    if (!selectedKb || !selectedAgent) return;
    const kbId = effectiveKbId(selectedKb);
    if (!kbId) {
      notifyError('Cannot assign: KB has no valid ID');
      return;
    }
    try {
      await axios.post(
        `/api/agents/${selectedAgent.value}/assign-kb/`,
        { kb_id: kbId },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      setAssignModalVisible(false);
      setSelectedAgent(null);
      notifySuccess('Knowledge base assigned to agent');
      fetchKBs();
    } catch (err) {
      notifyError('Assign failed: ' + (err.response?.data?.message || err.message));
    }
  };

  const openFiles = async (kb) => {
    setSelectedKb(kb);
    setFilesModalVisible(true);
    await fetchFiles(effectiveKbId(kb));
  };

  const fetchFiles = useCallback(async (kbId) => {
    try {
      setFilesLoading(true);
      const res = await axios.get(`/api/knowledge-bases/${kbId}/files/`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const data = res.data?.data;
      let raw;
      if (Array.isArray(data)) {
        raw = data;
      } else if (data && typeof data === 'object') {
        raw = data.files || data.items || data.documents || [];
      } else {
        raw = res.data?.files || [];
      }
      setFiles(Array.isArray(raw) ? raw : []);
    } catch (err) {
      console.error('File fetch error:', err);
      setFiles([]);
    } finally {
      setFilesLoading(false);
    }
  }, [accessToken]);

  const handleDeleteFile = async () => {
    if (!selectedKb || !selectedFile) return;
    const kbId = effectiveKbId(selectedKb);
    const fileId = selectedFile.id || selectedFile.file_id || selectedFile.document_id || selectedFile.adexcloud_file_id;
    if (!kbId || !fileId) {
      notifyError('Cannot delete: missing KB or file ID');
      return;
    }
    try {
      if (vectorOnly) {
        await axios.delete(
          `/api/knowledge-bases/${kbId}/vectordb-files/${fileId}/delete/`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
      } else {
        await axios.delete(
          `/api/knowledge-bases/${kbId}/files/${fileId}/delete/`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
      }
      setFiles((prev) => prev.filter((f) => (f.id || f.file_id || f.document_id || f.adexcloud_file_id) !== fileId));
      setFileDeleteModalVisible(false);
      notifySuccess('File deleted');
    } catch (err) {
      notifyError('Delete file failed: ' + (err.response?.data?.message || err.message));
    }
  };

  const openEdit = (kb) => {
    setSelectedKb(kb);
    setEditForm({
      name: kb.name || '',
      description: kb.description || '',
      status: kb.status || 'active',
    });
    setEditModalVisible(true);
  };

  const handleUpdate = async () => {
    if (!selectedKb) return;
    const kbId = effectiveKbId(selectedKb);
    if (!kbId) {
      notifyError('Cannot update: KB has no valid ID');
      return;
    }
    try {
      const payload = {
        name: editForm.name,
        description: editForm.description,
        status: editForm.status,
      };
      await axios.put(
        `/api/knowledge-bases/${kbId}/update/`,
        payload,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      setKbs((prev) =>
        prev.map((k) =>
          effectiveKbId(k) === kbId
            ? { ...k, ...payload }
            : k
        )
      );
      setEditModalVisible(false);
      notifySuccess('Knowledge base updated');
    } catch (err) {
      notifyError('Update failed: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleFileUpload = async (filesToUpload) => {
    if (!selectedKb || !filesToUpload.length) return;
    const kbId = effectiveKbId(selectedKb);
    if (!kbId) {
      notifyError('Cannot upload: KB has no valid ID');
      return;
    }
    for (const file of filesToUpload) {
      const tempId = `${file.name}-${Date.now()}`;
      setUploadQueue((prev) => [...prev, { id: tempId, name: file.name, status: 'uploading' }]);
      setUploadProgress((prev) => ({ ...prev, [tempId]: 0 }));
      try {
        const formData = new FormData();
        formData.append('file', file);
        const resp = await axios.post(`/api/knowledge-bases/${kbId}/upload/`, formData, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            const pct = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
            setUploadProgress((prev) => ({ ...prev, [tempId]: pct }));
          },
        });
        const serverFileId = resp.data?.data?.file_id;
        if (serverFileId) {
          setUploadQueue((prev) =>
            prev.map((f) => (f.id === tempId ? { ...f, serverFileId, status: 'pending' } : f))
          );
        } else {
          setUploadQueue((prev) =>
            prev.map((f) => (f.id === tempId ? { ...f, status: 'success' } : f))
          );
          fetchFiles(kbId);
        }
      } catch (err) {
        notifyError('Upload failed: ' + (err.response?.data?.message || err.message));
        setUploadQueue((prev) =>
          prev.map((f) => (f.id === tempId ? { ...f, status: 'failed' } : f))
        );
      }
    }
  };

  // Poll upload status for pending files
  useEffect(() => {
    if (!filesModalVisible) return;
    const pendingFiles = uploadQueue.filter(
      (f) => f.status === 'pending' && f.serverFileId
    );
    if (!pendingFiles.length) return;

    const kbId = selectedKb ? effectiveKbId(selectedKb) : null;
    if (!kbId) return;

    const interval = setInterval(async () => {
      for (const f of pendingFiles) {
        try {
          const resp = await axios.get(
            `/api/knowledge-bases/${kbId}/files/${f.serverFileId}/status/`,
            { headers: { Authorization: `Bearer ${accessToken}` } }
          );
          const data = resp.data?.data;
          const st = data?.status;
          // Store detailed file info for display
          setFileDetails((prev) => ({
            ...prev,
            [f.id]: {
              status: st,
              processing: data?.processing,
              cost_analysis: data?.cost_analysis,
              adexcloud_status: data?.adexcloud_status,
              updated_at: data?.updated_at,
            },
          }));
          if (st === 'success' || st === 'completed' || st === 'failed') {
            setUploadQueue((prev) =>
              prev.map((item) => (item.id === f.id ? { ...item, status: st } : item))
            );
            // Refresh file list on completion
            if (st === 'success' || st === 'completed') {
              fetchFiles(kbId);
            }
          }
        } catch (err) {
          console.error('Status poll error:', err);
        }
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [filesModalVisible, uploadQueue, selectedKb, accessToken, fetchFiles]);

  return (
    <AppLayoutWrapper
      breadcrumbs={
        <BreadcrumbGroup
          items={[
            { text: 'Home', href: '/dashboard' },
            { text: 'Knowledge Bases', href: '/knowledge-bases' },
          ]}
          onFollow={(event) => {
            event.preventDefault();
            navigate(event.detail.href);
          }}
        />
      }
      header={{
        title: 'Knowledge Bases',
        description: 'Manage knowledge bases attached to your AI agents.',
        actions: (
          <SpaceBetween direction="horizontal" size="xs">
            <Button iconName="refresh" onClick={fetchKBs}>
              Refresh
            </Button>
            <Button variant="primary" iconName="add-plus" onClick={() => navigate('/knowledge-base/new')}>
              Add Knowledge Base
            </Button>
          </SpaceBetween>
        ),
      }}
      headerBackgroundStyle="linear-gradient(to bottom, rgba(9, 29, 43, 1) 2%, rgba(0, 108, 224, 1) 8%, rgba(255, 255, 255, 1) 82%)"
      helpPanelHeader="Knowledge Bases"
      helpPanelContent={
        <div>
          <p>
            Knowledge bases provide contextual documents and information that
            your agents use to answer questions accurately.
          </p>
          <ul>
            <li>Upload PDFs, docs, and text files</li>
            <li>Attach KBs to one or more agents</li>
            <li>Monitor ingestion status</li>
          </ul>
        </div>
      }
    >
      {loading ? (
        <Box textAlign="center" padding={{ top: 'xxxl' }}>
          <Spinner size="large" />
        </Box>
      ) : error ? (
        <Alert type="error" header="Error loading knowledge bases">
          {error}
        </Alert>
      ) : (
        <SpaceBetween size="l">
          <ColumnLayout columns={{ default: 1, s: 2, m: 2, l: 4, xl: 4 }} variant="text-grid">
            <Container>
              <Box variant="awsui-key-label"><Icon name="file" variant="normal" /> Total KBs</Box>
              <Box variant="h1">{kbs.length}</Box>
            </Container>
            <Container>
              <Box variant="awsui-key-label"><Icon name="status-positive" variant="normal" /> Active</Box>
              <Box variant="h1" color="text-status-success">{kbs.filter((k) => k.is_active !== false).length}</Box>
            </Container>
            <Container>
              <Box variant="awsui-key-label"><Icon name="user-profile" variant="normal" /> Assigned to agents</Box>
              <Box variant="h1">{kbs.filter((k) => k.assistant || k.assistant_id || k.assistant_name).length}</Box>
            </Container>
            <Container>
              <Box variant="awsui-key-label"><Icon name="settings" variant="normal" /> Reserved on packs</Box>
              <Box variant="h1">{kbs.filter((k) => k.assigned_pack || k.assigned_pack_id || k.assigned_pack_name).length}</Box>
            </Container>
          </ColumnLayout>

          <Container
            header={
              <Header
                variant="h2"
                counter={`(${kbs.length})`}
                description="Document knowledge bases attached to your AI agents"
                actions={
                  <Button iconName="upload" onClick={() => navigate('/knowledge-base/new')}>
                    Upload File
                  </Button>
                }
              >
                Knowledge Bases
              </Header>
            }
          >
            <TextFilter
              filteringText={filteringText}
              filteringPlaceholder="Find knowledge bases by name or description"
              onChange={({ detail }) => setFilteringText(detail.filteringText)}
            />
            <Table
              columnDefinitions={[
                {
                  id: 'name',
                  header: 'Name',
                  cell: (item) => item.name || 'Unnamed KB',
                  sortingField: 'name',
                },
                {
                  id: 'description',
                  header: 'Description',
                  cell: (item) => item.description || 'N/A',
                  sortingField: 'description',
                },
                {
                  id: 'total_size',
                  header: 'Size',
                  cell: (item) => {
                    const size = item.total_size || item.adexcloud_data?.total_size;
                    if (!size) return '—';
                    const num = parseInt(size, 10);
                    if (isNaN(num)) return size;
                    return num > 1048576 ? `${(num / 1048576).toFixed(2)} MB` : `${num} B`;
                  },
                },
                {
                  id: 'assigned_to',
                  header: 'Assigned to',
                  cell: (item) => {
                    if (item.assistant_name || item.assistant?.name) {
                      return <StatusIndicator type="success">{item.assistant_name || item.assistant?.name}</StatusIndicator>;
                    }
                    if (item.assigned_pack_name || item.assigned_pack?.name) {
                      return <StatusIndicator type="info">{item.assigned_pack_name || item.assigned_pack?.name} (pack)</StatusIndicator>;
                    }
                    return <StatusIndicator type="pending">Unassigned</StatusIndicator>;
                  },
                },
                {
                  id: 'status',
                  header: 'Status',
                  sortingField: 'status',
                  cell: (item) => {
                    if (item.sync_status === 'failed' || (!item.adexcloud_kb_id && item.source === 'local')) {
                      return <StatusIndicator type="error">Sync failed</StatusIndicator>;
                    }
                    return (
                      <StatusIndicator type={item.is_active !== false ? 'success' : 'pending'}>
                        {item.is_active !== false ? 'Active' : 'Inactive'}
                      </StatusIndicator>
                    );
                  },
                },
                {
                  id: 'actions',
                  header: 'Actions',
                  cell: (item) => (
                    <SpaceBetween direction="horizontal" size="xs">
                      {(item.sync_status === 'failed' || (!item.adexcloud_kb_id && item.source === 'local')) && (
                        <Button
                          iconName="refresh"
                          variant="icon"
                          onClick={() => handleRetrySync(item)}
                          title="Retry sync to adexcloud"
                          loading={retryingId === item.id}
                        />
                      )}
                      <Button iconName="edit" variant="icon" onClick={() => openEdit(item)} title="Edit" />
                      <Button iconName="file" variant="icon" onClick={() => openFiles(item)} title="Manage files" />
                      <Button iconName="add-plus" variant="icon" onClick={() => openAssign(item)} title="Assign to agent" />
                      <Button iconName="remove" variant="icon" onClick={() => { setSelectedKb(item); setDeleteModalVisible(true); }} title="Delete" />
                    </SpaceBetween>
                  ),
                },
              ]}
              items={sortedKbs}
              loadingText="Loading knowledge bases"
              loading={loading}
              trackBy={(item) => item.id || item.adexcloud_kb_id}
              stickyHeader
              sortable
              sortingColumn={sortingColumn}
              sortingDescending={sortingDescending}
              onSortingChange={({ detail }) => {
                setSortingColumn(detail.sortingColumn);
                setSortingDescending(detail.isDescending);
              }}
              empty={
                <Box textAlign="center" padding="l">
                  <SpaceBetween size="m">
                    <div>
                      <Box variant="h1" textAlign="center"><Icon name="file" size="big" variant="normal" /></Box>
                      <b>No knowledge bases found</b>
                      <p>Create a knowledge base and assign it to an agent.</p>
                    </div>
                    <Button variant="primary" onClick={() => navigate('/knowledge-base/new')}>Add Knowledge Base</Button>
                  </SpaceBetween>
                </Box>
              }
            />
          </Container>
        </SpaceBetween>
      )}

      {/* Delete Modal */}
      {deleteModalVisible && (
        <Modal
          visible={deleteModalVisible}
          onDismiss={() => setDeleteModalVisible(false)}
          header="Delete Knowledge Base"
          footer={
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setDeleteModalVisible(false)}>Cancel</Button>
              <Button variant="primary" onClick={handleDelete}>Delete</Button>
            </SpaceBetween>
          }
        >
          Are you sure you want to delete <b>{selectedKb?.name}</b>?
        </Modal>
      )}

      {/* Assign Modal */}
      {assignModalVisible && (
        <Modal
          visible={assignModalVisible}
          onDismiss={() => setAssignModalVisible(false)}
          header="Assign Knowledge Base to Agent"
          footer={
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setAssignModalVisible(false)}>Cancel</Button>
              <Button variant="primary" onClick={handleAssign}>Assign</Button>
            </SpaceBetween>
          }
        >
          <SpaceBetween size="l">
            <p>Select an agent to assign <b>{selectedKb?.name}</b> to:</p>
            <Select
              placeholder="Choose agent"
              selectedOption={selectedAgent}
              onChange={({ detail }) => setSelectedAgent(detail.selectedOption)}
              options={agents}
            />
          </SpaceBetween>
        </Modal>
      )}

      {/* Files Modal */}
      {filesModalVisible && (
        <Modal
          visible={filesModalVisible}
          onDismiss={() => { setFilesModalVisible(false); setUploadQueue([]); setUploadProgress({}); }}
          header={`Files in ${selectedKb?.name}`}
          size="large"
          footer={
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => { setFilesModalVisible(false); setUploadQueue([]); setUploadProgress({}); }}>Close</Button>
            </SpaceBetween>
          }
        >
          <SpaceBetween size="m">
            <FileUpload
              value={pendingUploadFiles}
              onChange={({ detail }) => {
                setPendingUploadFiles(detail.value);
                if (detail.value.length > 0) {
                  handleFileUpload(detail.value);
                  setPendingUploadFiles([]);
                }
              }}
              constraintText="Upload files to this knowledge base."
              i18nStrings={{
                uploadButtonText: () => 'Upload File',
                dropzoneText: () => 'Drop file here',
                removeFileAriaLabel: (e) => `Remove ${e}`,
                errorIconAriaLabel: 'Error',
              }}
            />

            {/* Upload queue */}
            {uploadQueue.length > 0 && (
              <SpaceBetween size="xs">
                <Box variant="awsui-key-label">Uploading</Box>
                {uploadQueue.map((f) => {
                  const d = fileDetails[f.id];
                  const proc = d?.processing || (d?.adexcloud_status && d.adexcloud_status.processing);
                  const steps = [];
                  if (proc) {
                    if (proc.downloading !== undefined) steps.push({ label: 'Download', done: proc.downloading === true || proc.downloading === 'done' });
                    if (proc.extracting !== undefined) steps.push({ label: 'Extract', done: proc.extracting === true || proc.extracting === 'done' });
                    if (proc.chunking !== undefined) steps.push({ label: 'Chunk', done: proc.chunking === true || proc.chunking === 'done' });
                    if (proc.embedding !== undefined) steps.push({ label: 'Embed', done: proc.embedding === true || proc.embedding === 'done' });
                    if (proc.indexing !== undefined) steps.push({ label: 'Index', done: proc.indexing === true || proc.indexing === 'done' });
                    if (proc.uploading !== undefined) steps.push({ label: 'Upload', done: proc.uploading === true || proc.uploading === 'done' });
                  }
                  const cost = d?.cost_analysis;
                  return (
                    <div key={f.id} style={{ display: 'flex', flexDirection: 'column', gap: '4px', padding: '8px', border: '1px solid #d5dbdb', borderRadius: '6px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ flex: 1 }}>{f.name}</span>
                        {f.status === 'uploading' && (
                          <ProgressBar
                            value={uploadProgress[f.id] || 0}
                            label="Uploading..."
                            description={`${uploadProgress[f.id] || 0}%`}
                          />
                        )}
                        {f.status === 'pending' && (
                          <StatusIndicator type="pending">Processing</StatusIndicator>
                        )}
                        {f.status === 'success' && (
                          <StatusIndicator type="success">Ready</StatusIndicator>
                        )}
                        {f.status === 'completed' && (
                          <StatusIndicator type="success">Ready</StatusIndicator>
                        )}
                        {f.status === 'failed' && (
                          <StatusIndicator type="error">Failed</StatusIndicator>
                        )}
                      </div>
                      {f.status === 'pending' && steps.length > 0 && (
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginLeft: '4px' }}>
                          {steps.map((s) => (
                            <StatusIndicator key={s.label} type={s.done ? 'success' : 'in-progress'}>
                              {s.label}
                            </StatusIndicator>
                          ))}
                        </div>
                      )}
                      {f.status === 'pending' && proc?.error && (
                        <Box variant="small" color="text-status-error" margin={{ left: 'xs' }}>{proc.error}</Box>
                      )}
                      {f.status === 'pending' && cost && (
                        <Box variant="small" color="text-body-secondary" margin={{ left: 'xs' }}>
                          {cost.tokens ? `${cost.tokens} tokens` : ''}
                          {cost.tokens && cost.cost ? ' · ' : ''}
                          {cost.cost ? `${cost.cost} ${cost.currency || ''}` : ''}
                        </Box>
                      )}
                      {f.status === 'pending' && !steps.length && !cost && !proc?.error && (
                        <Box variant="small" color="text-body-secondary" margin={{ left: 'xs' }}>
                          Processing on adexcloud...
                        </Box>
                      )}
                    </div>
                  );
                })}
              </SpaceBetween>
            )}

            {filesLoading ? (
              <Box textAlign="center" padding="l"><Spinner size="large" /></Box>
            ) : (
              <Table
                columnDefinitions={[
                  { id: 'name', header: 'Name', cell: (item) => item.name || item.filename || item.file_name || item.original_name || 'Unnamed' },
                  { id: 'size', header: 'Size', cell: (item) => {
                    const sz = item.size || item.file_size || item.bytes;
                    if (!sz && sz !== 0) return 'N/A';
                    return formatBytes(typeof sz === 'string' ? parseInt(sz, 10) : sz);
                  }},
                  { id: 'status', header: 'Status', cell: (item) => {
                    const s = item.status || item.file_status || item.processing_status || 'Processing';
                    let type = 'pending';
                    if (s === 'success' || s === 'ready' || s === 'completed') type = 'success';
                    if (s === 'failed' || s === 'error') type = 'error';
                    if (s === 'uploading') type = 'in-progress';
                    return (
                      <StatusIndicator type={type}>{s}</StatusIndicator>
                    );
                  }},
                  { id: 'actions', header: 'Actions', cell: (item) => (
                    <SpaceBetween direction="horizontal" size="xs">
                      <Button iconName="remove" variant="icon" onClick={() => {
                        setSelectedFile(item);
                        setVectorOnly(false);
                        setFileDeleteModalVisible(true);
                      }} title="Delete from S3 + Vector DB" />
                      <Button iconName="close" variant="icon" onClick={() => {
                        setSelectedFile(item);
                        setVectorOnly(true);
                        setFileDeleteModalVisible(true);
                      }} title="Delete from Vector DB only" />
                    </SpaceBetween>
                  )},
                ]}
                items={files}
                trackBy={(item) => item.id || item.file_id || item.document_id || item.name}
                empty={
                  <Box textAlign="center" padding="l">
                    <SpaceBetween size="m">
                      <b>No files found</b>
                      <p>Upload files to this knowledge base.</p>
                    </SpaceBetween>
                  </Box>
                }
              />
            )}
          </SpaceBetween>
        </Modal>
      )}

      {/* File Delete Modal */}
      {fileDeleteModalVisible && (
        <Modal
          visible={fileDeleteModalVisible}
          onDismiss={() => setFileDeleteModalVisible(false)}
          header={vectorOnly ? 'Delete from Vector DB Only' : 'Delete File (S3 + Vector DB)'}
          footer={
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setFileDeleteModalVisible(false)}>Cancel</Button>
              <Button variant="primary" onClick={handleDeleteFile}>Delete</Button>
            </SpaceBetween>
          }
        >
          Are you sure you want to delete <b>{selectedFile?.name || selectedFile?.filename || selectedFile?.file_name || selectedFile?.original_name}</b>?
          {vectorOnly ? (
            <p>This will only remove the file from the vector database, keeping the original in S3.</p>
          ) : (
            <p>This will permanently delete the file from both S3 and the vector database.</p>
          )}
        </Modal>
      )}

      {/* Edit Modal */}
      {editModalVisible && (
        <Modal
          visible={editModalVisible}
          onDismiss={() => setEditModalVisible(false)}
          header="Edit Knowledge Base"
          footer={
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setEditModalVisible(false)}>Cancel</Button>
              <Button variant="primary" onClick={handleUpdate}>Save</Button>
            </SpaceBetween>
          }
        >
          <SpaceBetween size="l">
            <FormField label="Name" constraintText="Required">
              <Input
                value={editForm.name}
                onChange={({ detail }) => setEditForm((prev) => ({ ...prev, name: detail.value }))}
              />
            </FormField>
            <FormField label="Description">
              <Textarea
                value={editForm.description}
                onChange={({ detail }) => setEditForm((prev) => ({ ...prev, description: detail.value }))}
              />
            </FormField>
          </SpaceBetween>
        </Modal>
      )}
    </AppLayoutWrapper>
  );
}
